'use client';

import Link from 'next/link';
import { CircleAlert, LogIn, Mail, RefreshCw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Loader } from '@/components/ui/loader';
import { Skeleton } from '@/components/ui/skeleton';
import type { getPostHogSupportCopy } from '@/i18n/posthog-support-copy';
import { cn } from '@/lib/utils';

import {
  PostHogSupportAskImai,
  useAskImaiHref,
} from './posthog-support-ask-imai';
import {
  getPostHogSupportContentWidth,
  getPostHogSupportHorizontalPadding,
} from './posthog-support-layout';

type SupportCopy = ReturnType<typeof getPostHogSupportCopy>;

export function PostHogSupportLoading({ copy }: { copy: SupportCopy }) {
  return (
    <div
      role="status"
      aria-label={copy.loading}
      className="flex min-h-0 flex-1 flex-col p-4"
    >
      <div className="space-y-2">
        <Skeleton className="h-16 w-full rounded-lg" />
        <Skeleton className="h-16 w-full rounded-lg" />
        <Skeleton className="h-16 w-full rounded-lg" />
      </div>
    </div>
  );
}

/**
 * Ticket support is down, but Ask IMAI is a plain link to Your IMAI, so it
 * stays on top: the member can still get an answer right away. With it, the
 * notice follows directly below as one column; without a workspace (no Ask
 * IMAI) the notice stays centered on its own.
 */
export function PostHogSupportUnavailable({
  compact,
  copy,
  dialog,
  isRetrying,
  onRetry,
}: {
  compact: boolean;
  copy: SupportCopy;
  dialog: boolean;
  isRetrying: boolean;
  onRetry: () => void | Promise<void>;
}) {
  const stacked = useAskImaiHref() !== null;
  const notice = (
    <PostHogSupportUnavailableNotice
      copy={copy}
      isRetrying={isRetrying}
      onRetry={onRetry}
      stacked={stacked}
    />
  );
  if (!stacked) return notice;
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
      <div
        className={cn(
          getPostHogSupportHorizontalPadding(compact),
          compact ? 'pt-3' : 'pt-6 sm:pt-10'
        )}
      >
        <div
          className={cn(
            getPostHogSupportContentWidth(compact),
            compact && 'border-border/35 overflow-hidden rounded-xl border'
          )}
        >
          <PostHogSupportAskImai
            compact={compact}
            copy={copy}
            dialog={dialog}
          />
        </div>
      </div>
      {notice}
    </div>
  );
}

function PostHogSupportUnavailableNotice({
  copy,
  isRetrying,
  onRetry,
  stacked,
}: {
  copy: SupportCopy;
  isRetrying: boolean;
  onRetry: () => void | Promise<void>;
  /** Directly below Ask IMAI with a section gap, not centered in the rest. */
  stacked: boolean;
}) {
  return (
    <div
      className={cn(
        'flex justify-center px-6',
        stacked ? 'pt-8 pb-10' : 'min-h-0 flex-1 items-center py-10'
      )}
      role="status"
    >
      <div className="max-w-sm text-center">
        <span className="bg-muted text-muted-foreground mx-auto flex size-10 items-center justify-center rounded-xl">
          <CircleAlert className="size-5" aria-hidden />
        </span>
        <h2
          className="mt-3 text-sm font-medium text-balance"
          data-support-view-heading
          tabIndex={-1}
        >
          {copy.unavailable}
        </h2>
        <a
          href="mailto:contact@imai.studio"
          className="text-primary focus-visible:ring-ring mt-2 inline-flex min-h-11 items-center gap-1.5 rounded-md px-2 text-sm underline-offset-4 hover:underline focus-visible:ring-2 focus-visible:outline-none"
        >
          <Mail className="size-3.5" aria-hidden />
          {copy.contactEmail}
        </a>
        <div className="mt-4">
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="min-h-11 aria-disabled:cursor-wait aria-disabled:opacity-60 sm:min-h-8"
            aria-disabled={isRetrying}
            aria-busy={isRetrying}
            onClick={() => {
              if (isRetrying) return;
              void onRetry();
            }}
          >
            {isRetrying ? (
              <span aria-hidden="true">
                <Loader />
              </span>
            ) : (
              <RefreshCw className="size-3.5" aria-hidden />
            )}
            {copy.retry}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function PostHogSupportSignedOut({
  copy,
  returnTo,
}: {
  copy: SupportCopy;
  returnTo: string;
}) {
  return (
    <div
      className="flex min-h-0 flex-1 items-center justify-center px-6 py-10"
      role="status"
    >
      <div className="max-w-sm text-center">
        <span className="bg-muted text-muted-foreground mx-auto flex size-10 items-center justify-center rounded-xl">
          <LogIn className="size-5" aria-hidden />
        </span>
        <h2
          className="mt-3 text-sm font-medium text-balance"
          data-support-view-heading
          tabIndex={-1}
        >
          {copy.signIn}
        </h2>
        <p className="text-muted-foreground mt-1 text-sm text-pretty">
          {copy.signedOutDescription}
        </p>
        <Button asChild size="sm" className="mt-4 min-h-11 sm:min-h-8">
          <Link href={`/login?returnTo=${encodeURIComponent(returnTo)}`}>
            {copy.signIn}
          </Link>
        </Button>
      </div>
    </div>
  );
}
